import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeamLeads, getAgenturLeadStats, getUserSubTeamInfo, getSubTeamsByTeam, getTeamMembers } from '../../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SubTeamDashboard from './SubTeamDashboard';
import DateRangeFilter from '../common/DateRangeFilter';

function TeamDashboard({ user, selectedTeam, onBackToOrganization }) {
  // Date Range State - Standard: Letzte 14 Tage
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      days: 14
    };
  });

  const [teamStats, setTeamStats] = useState({
    totalLeads: 0,
    qualified: 0,
    noInterest: 0,
    revoked: 0,
    notQualified: 0,
    loading: true,
    error: null
  });

  const [chartData, setChartData] = useState({
    allPerformers: [],
    topPerformers: [],
    dailyLeads: [],
    loading: true
  });

  // Pagination State für Mitarbeiter Performance
  const [performerIndex, setPerformerIndex] = useState(0);
  const performersPerPage = 3;

  // Reset pagination index when data changes
  useEffect(() => {
    setPerformerIndex(0);
  }, [chartData.allPerformers.length]);

  // Ensure performerIndex doesn't exceed available pages
  useEffect(() => {
    const totalPages = getTotalPages();
    if (performerIndex >= totalPages) {
      setPerformerIndex(Math.max(0, totalPages - 1));
    }
  }, [performerIndex, chartData.allPerformers.length]);

  // State für Team-Mitglieder
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [userSubTeamInfo, setUserSubTeamInfo] = useState(null);
  const [subTeams, setSubTeams] = useState([]);
  const [subTeamsLoading, setSubTeamsLoading] = useState(false);
  const [selectedSubTeam, setSelectedSubTeam] = useState(null);

  const navigate = useNavigate();

  // Pagination Funktionen für Mitarbeiter Performance
  const getCurrentPagePerformers = () => {
    const startIndex = performerIndex * performersPerPage;
    const endIndex = startIndex + performersPerPage;
    return chartData.allPerformers.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const totalPages = Math.ceil(chartData.allPerformers.length / performersPerPage);
    return Math.max(1, totalPages); // Mindestens 1 Seite
  };

  const handlePreviousPage = () => {
    setPerformerIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    setPerformerIndex(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Bestimme ob User Teamleiter ist oder ob ein Team ausgewählt wurde (für Org-Leader)
  const isTeamLeader = user?.isTeamlead || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isAdmin = user?.isAdmin || false;
  const isAgenturLeader = user?.isAgenturLeader || false;
  
  // Verwende das ausgewählte Team oder das User-Team
  const currentTeam = selectedTeam || user?.team;
  const canViewTeamData = isTeamLeader || isOrgLeader || isAdmin || isAgenturLeader;

  // Prüfe ob wir eine Agentur-ID haben (für Agentur-Team-Dashboard)
  const isAgenturTeamView = currentTeam?.agentur_id;

  // Sub-Team-Informationen laden
  useEffect(() => {
    const loadUserSubTeamInfo = async () => {
      if (!user?.id) return;
      
      try {
        const data = await getUserSubTeamInfo(user.id);
        if (data.success) {
          setUserSubTeamInfo(data.userInfo);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Sub-Team-Informationen:", err);
      }
    };

    loadUserSubTeamInfo();
  }, [user?.id]);

  // Sub-Teams für das Team laden
  useEffect(() => {
    const loadSubTeams = async () => {
      if (!user?.id || !currentTeam?.id) return;
      
      setSubTeamsLoading(true);
      try {
        const response = await getSubTeamsByTeam(user.id, currentTeam.id);
        if (response.success) {
          setSubTeams(response.subTeams || []);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Sub-Teams:", err);
      } finally {
        setSubTeamsLoading(false);
      }
    };

    loadSubTeams();
  }, [user?.id, currentTeam?.id]);

  // Team-Mitglieder laden
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!canViewTeamData || !currentTeam?.id) return;
      
      setMembersLoading(true);
      try {
        let response;
        if (isAgenturTeamView) {
          // Für Agentur-Team-View: Verwende agentur_id und team_id (inkl. deaktivierte für Statistik)
          response = await getTeamMembers(user.id, currentTeam.id, currentTeam.agentur_id, true);
        } else {
          // Für normales Team-View: Verwende nur team_id (inkl. deaktivierte für Statistik)
          response = await getTeamMembers(user.id, currentTeam.id, null, true);
        }
        
        if (response.success) {
          setTeamMembers(response.members || []);
        } else {
          console.error("Fehler beim Laden der Team-Mitglieder:", response.error);
          setTeamMembers([]);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Team-Mitglieder:", err);
        setTeamMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };

    loadTeamMembers();
  }, [canViewTeamData, user?.id, currentTeam?.id, isAgenturTeamView]);

  // Chart-Daten verarbeiten (außerhalb des useEffect definiert)
  const processChartData = (allLeads) => {
    // Prüfen ob Team-Mitglieder geladen sind
    if (!teamMembers || teamMembers.length === 0) {
      console.log("Keine Team-Mitglieder verfügbar, warte auf Daten...");
      return;
    }

    // Top Performer Daten generieren
    const userLeadsMap = {};
    
    allLeads.forEach(lead => {
      // Exakte Zuordnung über die User-ID (Name/Dienstleister ist nicht zuverlässig)
      const ownerId = lead.user_id;
      if (ownerId == null) return;

      if (!userLeadsMap[ownerId]) {
        userLeadsMap[ownerId] = { total: 0, qualified: 0 };
      }
      userLeadsMap[ownerId].total++;

      // Qualifiziert: pro Lead einmal (PV oder WP)
      const isQualified =
        (lead.status_pv && lead.status_pv.toLowerCase().includes('qualifiziert')) ||
        (lead.status_wp && lead.status_wp.toLowerCase().includes('qualifiziert'));

      if (isQualified) {
        userLeadsMap[ownerId].qualified++;
      }
    });

    // Alle Team-Mitglieder mit ihren Lead-Daten erstellen
    const allPerformers = teamMembers.map(member => {
      const memberName = member.full_name || member.email || 'Unbekannt';
      const leadData = userLeadsMap[member.id] || { total: 0, qualified: 0 };
      
      return {
        name: memberName.length > 12 ? memberName.substring(0, 12) + '...' : memberName,
        fullName: memberName,
        Gesamt: leadData.total,
        Qualifiziert: leadData.qualified,
        memberId: member.id,
        role: member.role_name || 'Benutzer'
      };
    }).sort((a, b) => b.Gesamt - a.Gesamt);

    // Top 3 Performer für die erste Seite
    const topPerformers = allPerformers.slice(0, 3);

    // Daily Leads für letzte 60 Tage
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    
    const dailyLeads = [];
    for (let i = 0; i < 60; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayLeads = allLeads.filter(lead => {
        const leadDate = new Date(lead.created_at);
        return leadDate.toISOString().split('T')[0] === dateStr;
      });
      
      dailyLeads.unshift({
        date: dateStr,
        leads: dayLeads.length
      });
    }

    setChartData({
      allPerformers,
      topPerformers,
      dailyLeads,
      loading: false
    });
  };

  // Lead-Statistiken laden
  useEffect(() => {
    const loadTeamStatistics = async () => {
      if (!canViewTeamData || !currentTeam?.id) {
        setTeamStats(prev => ({ ...prev, loading: false }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        setTeamStats(prev => ({ ...prev, loading: true, error: null }));
        setChartData(prev => ({ ...prev, loading: true }));
        
        let response;
        if (isAgenturTeamView) {
          response = await getAgenturLeadStats(user.id, currentTeam.agentur_id, currentTeam.id, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
        } else {
          response = await getTeamLeads(user.id, currentTeam.id);
        }
        
        if (response.success) {
          if (isAgenturTeamView) {
            const s = response.stats || {};
            setTeamStats({
              totalLeads: typeof s.totalLeads === 'number' ? s.totalLeads : 0,
              qualified: s.qualified ?? 0,
              noInterest: s.noInterest ?? 0,
              revoked: s.revoked ?? 0,
              notQualified: s.notQualified ?? 0,
              loading: false,
              error: null,
            });
            processAgenturTeamStats(response);
          } else {
            const leads = response.leads || [];
            const recentLeads = leads.filter((lead) => {
                const startDate = new Date(dateRange.startDate);
                const endDate = new Date(dateRange.endDate);
                endDate.setHours(23, 59, 59, 999);
                const leadDate = new Date(lead.erstellt);
                return leadDate >= startDate && leadDate <= endDate;
              });
            const totalInRange = recentLeads.length;
            const stats = {
              totalLeads: totalInRange,
              qualified: 0,
              noInterest: 0,
              revoked: 0,
              notQualified: 0,
              loading: false,
              error: null
            };
            recentLeads.forEach(lead => {
              const pvStatus = (lead.status_pv || '').toLowerCase();
              const wpStatus = (lead.status_wp || '').toLowerCase();

              // Qualifiziert: pro Lead nur EINMAL zählen (PV+WP-Lead nicht doppelt),
              // damit die Gesamtsumme zur Summe der Mitarbeiter-Balken passt.
              if (pvStatus.includes('qualifiziert') || wpStatus.includes('qualifiziert')) {
                stats.qualified++;
              }

              // Übrige Kategorien wie bisher (je Produkt-Status), aber ohne Qualifiziert
              if (lead.status_pv) {
                if (pvStatus.includes('kein interesse')) {
                  stats.noInterest++;
                } else if (pvStatus.includes('widerruf')) {
                  stats.revoked++;
                } else if (pvStatus.includes('test lead') || pvStatus.includes('telefonnummer ungültig') || lead.status_pv_invalid) {
                  stats.notQualified++;
                }
              }
              if (lead.status_wp) {
                if (wpStatus.includes('kein interesse')) {
                  stats.noInterest++;
                } else if (wpStatus.includes('widerruf')) {
                  stats.revoked++;
                } else if (wpStatus.includes('test lead') || wpStatus.includes('telefonnummer ungültig') || lead.status_wp_invalid) {
                  stats.notQualified++;
                }
              }
            });
            setTeamStats(stats);
            processChartData(leads);
          }
        } else {
          setTeamStats(prev => ({
            ...prev,
            loading: false,
            error: response.error || 'Fehler beim Laden der Team-Statistiken'
          }));
          if (teamMembers && teamMembers.length > 0) {
            if (isAgenturTeamView) {
              processAgenturTeamStats({ daily: [], byUser: [] });
            } else {
              processChartData([]);
            }
          } else {
            setChartData(prev => ({ ...prev, loading: false }));
          }
        }
      } catch (err) {
        setTeamStats(prev => ({
          ...prev,
          loading: false,
          error: 'Netzwerkfehler: ' + err.message
        }));
        if (teamMembers && teamMembers.length > 0) {
          if (isAgenturTeamView) {
            processAgenturTeamStats({ daily: [], byUser: [] });
          } else {
            processChartData([]);
          }
        } else {
          setChartData(prev => ({ ...prev, loading: false }));
        }
      }
    };

    const processAgenturTeamStats = (res) => {
      const byUser = res?.byUser || [];
      const daily = res?.daily || [];
      if (!teamMembers || teamMembers.length === 0) {
        setChartData((prev) => ({
          ...prev,
          dailyLeads: daily,
          allPerformers: [],
          topPerformers: [],
          loading: true,
        }));
        return;
      }
      const umap = new Map(byUser.map((u) => [u.userId, u]));
      const allPerformers = teamMembers
        .map((member) => {
          const memberName = member.full_name || member.email || 'Unbekannt';
          const leadData = umap.get(member.id) || { total: 0, qualified: 0 };
          return {
            name: memberName.length > 12 ? memberName.substring(0, 12) + '...' : memberName,
            fullName: memberName,
            Gesamt: leadData.total,
            Qualifiziert: leadData.qualified,
            memberId: member.id,
            role: member.role_name || 'Benutzer',
          };
        })
        .sort((a, b) => b.Gesamt - a.Gesamt);
      const topPerformers = allPerformers.slice(0, 3);
      const dailyLeads = daily.map((d) => ({ date: d.date, leads: d.leads }));
      setChartData({
        allPerformers,
        topPerformers,
        dailyLeads,
        loading: false,
      });
    };

    const processChartData = (allLeads) => {
      if (!teamMembers || teamMembers.length === 0) {
        return;
      }
      const userLeadsMap = {};
      allLeads.forEach(lead => {
        // Exakte Zuordnung über die User-ID (Name/Dienstleister ist nicht zuverlässig)
        const ownerId = lead.user_id;
        if (ownerId == null) return;
        if (!userLeadsMap[ownerId]) {
          userLeadsMap[ownerId] = { total: 0, qualified: 0 };
        }
        userLeadsMap[ownerId].total++;
        const isQualified =
          (lead.status_pv && lead.status_pv.toLowerCase().includes('qualifiziert')) ||
          (lead.status_wp && lead.status_wp.toLowerCase().includes('qualifiziert'));
        if (isQualified) {
          userLeadsMap[ownerId].qualified++;
        }
      });
      const allPerformers = teamMembers.map(member => {
        const memberName = member.full_name || member.email || 'Unbekannt';
        const leadData = userLeadsMap[member.id] || { total: 0, qualified: 0 };
        return {
          name: memberName.length > 12 ? memberName.substring(0, 12) + '...' : memberName,
          fullName: memberName,
          Gesamt: leadData.total,
          Qualifiziert: leadData.qualified,
          memberId: member.id,
          role: member.role_name || 'Benutzer'
        };
      }).sort((a, b) => b.Gesamt - a.Gesamt);
      const topPerformers = allPerformers.slice(0, 3);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      const dailyLeadsMap = {};
      for (let i = 59; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit'
        });
        dailyLeadsMap[dateStr] = 0;
      }
      allLeads.forEach(lead => {
        const leadDate = new Date(lead.erstellt);
        if (leadDate >= sixtyDaysAgo) {
          const dateStr = leadDate.toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit'
          });
          if (Object.prototype.hasOwnProperty.call(dailyLeadsMap, dateStr)) {
            dailyLeadsMap[dateStr]++;
          }
        }
      });
      const dailyLeads = Object.entries(dailyLeadsMap).map(([date, count]) => ({
        date,
        leads: count
      }));
      setChartData({
        allPerformers,
        topPerformers,
        dailyLeads,
        loading: false
      });
    };

    loadTeamStatistics();
  }, [canViewTeamData, user?.id, currentTeam?.id, isAgenturTeamView, teamMembers, dateRange]);


  // Custom Tooltip für Top Performer Chart
  const TopPerformerTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          fontSize: '14px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#2d3748' }}>
            {data.fullName}
          </p>
          <p style={{ margin: '0 0 4px 0', color: '#4a6de5' }}>
            ■ Gesamt: {data.Gesamt}
          </p>
          <p style={{ margin: '0', color: '#1a365d' }}>
            ■ Qualifiziert: {data.Qualifiziert}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip für Daily Leads Chart
  const DailyLeadsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          fontSize: '14px'
        }}>
          <p style={{ margin: '0', color: '#2d3748' }}>
            {label}: {payload[0].value} Leads
          </p>
        </div>
      );
    }
    return null;
  };

  // Prozentsatz berechnen
  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Wenn ein Sub-Team ausgewählt ist, zeige Sub-Team-Dashboard
  if (selectedSubTeam) {
    return (
      <SubTeamDashboard 
        user={user}
        selectedSubTeam={selectedSubTeam}
        onBackToTeam={() => setSelectedSubTeam(null)}
      />
    );
  }

  // Wenn keine Berechtigung, zeige Meldung
  if (!canViewTeamData) {
    return (
      <div className="team-dashboard-container">
        <div className="team-dashboard-header">
          <h2>Team Dashboard</h2>
        </div>
        <div className="no-team-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <p>Team Dashboard ist nur für Teamleiter verfügbar.</p>
          <span>Sie haben keine Berechtigung, Team-Statistiken einzusehen.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="team-dashboard-container">
      <div className="team-dashboard-header">
        {/* Back Button für Org-Leader oder Teamleiter/Agenturleiter in Agenturansicht */}
        {selectedTeam && (onBackToOrganization || isAgenturTeamView) && (
          <button 
            onClick={
              isAgenturTeamView && (isTeamLeader || isOrgLeader)
                ? () => navigate(-1)
                : onBackToOrganization
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#374151',
              marginBottom: '16px',
              transition: 'all 0.2s ease',
              fontWeight: '500',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              width: 'fit-content',
              maxWidth: '20%',
              minWidth: '120px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
            {isAgenturTeamView && (isTeamLeader || isOrgLeader) ? 'Zurück' : 'Zurück zur Organisation'}
          </button>
        )}
        
        <h2>
          {userSubTeamInfo?.sub_team_name ? 
            `Sub-Team Dashboard - ${currentTeam?.name || 'Mein Team'} (${userSubTeamInfo.sub_team_name})` : 
            `Team Dashboard - ${currentTeam?.name || 'Mein Team'}`
          }
        </h2>
        <span className="team-name">
          {userSubTeamInfo?.sub_team_name ? 
            `${currentTeam?.name || 'Mein Team'} (${userSubTeamInfo.sub_team_name})` : 
            currentTeam?.name || 'Mein Team'
          }
        </span>
      </div>
      
      {/* Date Range Filter */}
      <DateRangeFilter 
        onDateRangeChange={setDateRange}
        initialDays={14}
      />
      
      {teamStats.loading ? (
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <span>Lade Team-Statistiken...</span>
        </div>
      ) : teamStats.error ? (
        <div className="stats-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>{teamStats.error}</span>
        </div>
      ) : (
        <>
          {/* Sub-Teams Sektion - für Teamleiter */}
          {isTeamLeader && subTeams.length > 0 && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Sub-Teams verwalten
              </h3>
              
              {subTeamsLoading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Sub-Teams...</span>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '16px'
                }}>
                  {subTeams.map(subTeam => (
                    <div
                      key={subTeam.id}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: selectedSubTeam?.id === subTeam.id ? '#f7fafc' : '#ffffff'
                      }}
                      onClick={() => setSelectedSubTeam(selectedSubTeam?.id === subTeam.id ? null : subTeam)}
                      onMouseOver={(e) => {
                        e.target.style.borderColor = '#4a6de5';
                        e.target.style.boxShadow = '0 2px 8px rgba(74, 109, 229, 0.15)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <h4 style={{
                          margin: '0',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#2d3748'
                        }}>
                          {subTeam.name}
                        </h4>
                        <span style={{
                          backgroundColor: '#4a6de5',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {subTeam.member_count || 0} Mitglieder
                        </span>
                      </div>
                      
                      {subTeam.description && (
                        <p style={{
                          margin: '0 0 12px 0',
                          fontSize: '0.875rem',
                          color: '#718096',
                          lineHeight: '1.4'
                        }}>
                          {subTeam.description}
                        </p>
                      )}
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#a0aec0'
                      }}>
                        <span>Team: {subTeam.team_name}</span>
                        <span>ID: {subTeam.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="team-stats-grid">
            {/* Gesamte Leads */}
            <div className="stat-card total-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{teamStats.totalLeads}</div>
                <div className="stat-label">Leads eingegangen</div>
              </div>
            </div>
            
            {/* Qualifizierte Leads */}
            <div className="stat-card qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{teamStats.qualified}</div>
                <div className="stat-label">Qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(teamStats.qualified, teamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Kein Interesse */}
            <div className="stat-card no-interest-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{teamStats.noInterest}</div>
                <div className="stat-label">Kein Interesse</div>
                <div className="stat-percentage">
                  {getPercentage(teamStats.noInterest, teamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Widerrufen */}
            <div className="stat-card revoked-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18l-2 13H5L3 6z"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{teamStats.revoked}</div>
                <div className="stat-label">Widerrufen</div>
                <div className="stat-percentage">
                  {getPercentage(teamStats.revoked, teamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Nicht qualifiziert */}
            <div className="stat-card not-qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{teamStats.notQualified}</div>
                <div className="stat-label">Nicht qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(teamStats.notQualified, teamStats.totalLeads)}%
                </div>
              </div>
            </div>
          </div>

          {/* Chart-Sektion */}
          <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr',
          gap: '24px', 
          marginTop: '32px'
          }}>
            {/* Top Performer Chart */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ 
                  margin: '0', 
                  fontSize: '1.125rem', 
                  fontWeight: '600', 
                  color: '#2d3748' 
                }}>
                  Mitarbeiter Performance
                </h3>
                {chartData.allPerformers.length > performersPerPage && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <button
                      onClick={handlePreviousPage}
                      disabled={performerIndex === 0}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        backgroundColor: performerIndex === 0 ? '#f7fafc' : 'white',
                        color: performerIndex === 0 ? '#a0aec0' : '#4a5568',
                        cursor: performerIndex === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (performerIndex > 0) {
                          e.target.style.backgroundColor = '#f7fafc';
                          e.target.style.borderColor = '#4a6de5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (performerIndex > 0) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      ← Zurück
                    </button>
                    <span style={{
                      fontSize: '14px',
                      color: '#718096',
                      minWidth: '80px',
                      textAlign: 'center'
                    }}>
                      {performerIndex + 1} / {getTotalPages()}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={performerIndex >= getTotalPages() - 1}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        backgroundColor: performerIndex >= getTotalPages() - 1 ? '#f7fafc' : 'white',
                        color: performerIndex >= getTotalPages() - 1 ? '#a0aec0' : '#4a5568',
                        cursor: performerIndex >= getTotalPages() - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (performerIndex < getTotalPages() - 1) {
                          e.target.style.backgroundColor = '#f7fafc';
                          e.target.style.borderColor = '#4a6de5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (performerIndex < getTotalPages() - 1) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      Weiter →
                    </button>
                  </div>
                )}
              </div>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : getCurrentPagePerformers().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={getCurrentPagePerformers()} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                      interval={0}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                    />
                    <Tooltip content={<TopPerformerTooltip />} />
                    <Bar 
                      dataKey="Gesamt" 
                      fill="#4a6de5" 
                      radius={[4, 4, 0, 0]}
                      name="Gesamt"
                    />
                    <Bar 
                      dataKey="Qualifiziert" 
                      fill="#1a365d" 
                      radius={[4, 4, 0, 0]}
                      name="Qualifiziert"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#a0aec0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <path d="M9 9h6v6H9z"></path>
                  </svg>
                  <span>Keine Team-Daten verfügbar</span>
                </div>
              )}
            </div>

            {/* Daily Leads Chart */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748' 
              }}>
                Leads pro Tag
              </h3>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : chartData.dailyLeads.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={chartData.dailyLeads} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#4a5568' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                    />
                    <Tooltip content={<DailyLeadsTooltip />} />
                    <Bar 
                      dataKey="leads" 
                      fill="#4a6de5" 
                      radius={[2, 2, 0, 0]}
                      name="Leads"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#a0aec0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <path d="M9 9h6v6H9z"></path>
                  </svg>
                  <span>Keine Daily-Daten verfügbar</span>
                </div>
              )}
            </div>
          </div>

          {/* Zusätzliche Informationen */}
          {teamStats.totalLeads > 0 && (
            <div className="team-insights">
              <h3>Insights</h3>
              <div className="insights-grid">
                <div className="insight-item">
                  <span className="insight-label">Erfolgsrate:</span>
                  <span className="insight-value qualified-text">
                    {getPercentage(teamStats.qualified, teamStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Verlustrate:</span>
                  <span className="insight-value negative-text">
                    {getPercentage(teamStats.noInterest + teamStats.revoked + teamStats.notQualified, teamStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Durchschnitt/Tag:</span>
                  <span className="insight-value neutral-text">
                    {Math.round(teamStats.totalLeads / 30)} Leads
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Top Performer:</span>
                  <span className="insight-value neutral-text">
                    {chartData.topPerformers.length > 0 ? chartData.topPerformers[0].fullName : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Kein Data Zustand */}
      {!teamStats.loading && !teamStats.error && teamStats.totalLeads === 0 && (
        <div className="no-team-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
            <line x1="9" y1="9" x2="9.01" y2="9"></line>
            <line x1="15" y1="9" x2="15.01" y2="9"></line>
          </svg>
          <p>Keine Leads in den letzten 2 Wochen gefunden.</p>
          <span>Beginnen Sie mit der Lead-Generierung für Ihr Team!</span>
        </div>
      )}
    </div>
  );
}

export default TeamDashboard;